<template>
	<v-card flat class="status-admin-card">
		<v-card-text class="pa-3">
			<!-- Live Banner Preview -->
			<div v-if="form.banner && form.bannerText" class="preview-wrapper mb-2">
				<span class="preview-label">{{ $t('preview') }}</span>
				<div class="status-preview" :class="statusPreviewClass">
					<v-icon color="white" size="16">{{ currentStatusConfig.icon }}</v-icon>
					<span v-if="previewCountdownText" class="countdown-badge">{{ previewCountdownText }}</span>
					<span class="preview-text">{{ form.bannerText || $t('banner-placeholder') }}</span>
					<v-icon
						v-if="form.dismissable"
						color="white"
						size="14"
						class="dismiss-icon"
					>
						mdi-close
					</v-icon>
				</div>
			</div>

			<!-- Master Enable -->
			<div class="master-toggle mb-2">
				<v-switch
					v-model="form.enabled"
					inset
					hide-details
					dense
					class="mt-0"
				>
					<template #label>
						<span class="font-weight-medium text-body-2">{{ $t('status-system-enabled') }}</span>
					</template>
				</v-switch>
			</div>

			<v-expand-transition>
				<div v-show="form.enabled">
					<!-- Status Selection -->
					<div class="section-card mb-2">
						<div class="section-header">
							<v-icon x-small class="mr-1">mdi-traffic-light</v-icon>
							{{ $t('system-status') }}
						</div>
						<div class="status-buttons">
							<button
								v-for="opt in statusOptions"
								:key="opt.value"
								type="button"
								class="status-btn"
								:class="{ active: form.status === opt.value }"
								:style="form.status === opt.value ? `--active-color: ${opt.hex}` : ''"
								@click="form.status = opt.value"
							>
								<v-icon x-small :color="form.status === opt.value ? 'white' : opt.color">
									{{ opt.icon }}
								</v-icon>
								<span>{{ opt.label }}</span>
							</button>
						</div>
					</div>

					<!-- Banner Configuration -->
					<div class="section-card mb-2">
						<div class="section-header">
							<v-icon x-small class="mr-1">mdi-message-alert-outline</v-icon>
							{{ $t('banner-configuration') }}
						</div>
						<div class="section-content">
							<div class="toggle-row">
								<v-switch
									v-model="form.banner"
									dense
									hide-details
									class="mt-0 compact-switch"
								>
									<template #label>
										<span class="text-caption">{{ $t('show-banner') }}</span>
									</template>
								</v-switch>
								<v-switch
									v-model="form.dismissable"
									dense
									hide-details
									class="mt-0 ml-4 compact-switch"
									:disabled="!form.banner"
								>
									<template #label>
										<span class="text-caption">{{ $t('dismissable') }}</span>
									</template>
								</v-switch>
							</div>
							<v-text-field
								v-model="form.bannerText"
								:label="$t('banner-message')"
								:placeholder="$t('banner-placeholder')"
								outlined
								dense
								hide-details
								class="mt-2"
								:disabled="!form.banner"
							/>
						</div>
					</div>

					<!-- Overlay Configuration -->
					<div class="section-card mb-2">
						<div class="section-header">
							<v-icon x-small class="mr-1">mdi-lock-outline</v-icon>
							{{ $t('overlay-configuration') }}
						</div>
						<div class="section-content">
							<v-switch
								v-model="form.overlay"
								dense
								hide-details
								class="mt-0 compact-switch"
							>
								<template #label>
									<span class="text-caption">{{ $t('block-application') }}</span>
									<v-menu
										v-model="overlayPreviewOpen"
										:close-on-content-click="false"
										:open-on-hover="!$vuetify.breakpoint.xs"
										offset-y
										top
										max-width="400"
									>
										<template #activator="{ on, attrs }">
											<v-chip
												x-small
												class="ml-2"
												color="error"
												text-color="white"
												v-bind="attrs"
												v-on="on"
												@click.stop="overlayPreviewOpen = !overlayPreviewOpen"
											>
												{{ $t('full-page') }}
											</v-chip>
										</template>
										<div class="overlay-preview-wrapper" :class="previewStatusClass">
											<span class="overlay-preview-label">{{ $t('preview') }}</span>
											<div class="overlay-preview-content">
												<div class="preview-icon-container" :class="previewStatusClass">
													<v-icon size="48" color="white">{{ currentStatusConfig.icon }}</v-icon>
												</div>

												<h2 class="preview-title">
													{{ previewOverlayTitle }}
												</h2>

												<p v-if="form.bannerText" class="preview-message">
													{{ form.bannerText }}
												</p>

												<div v-if="maintenanceWindow && maintenanceWindow.minutesRemaining" class="preview-countdown">
													<v-icon color="white" size="18" class="mr-2">mdi-clock-outline</v-icon>
													<span>{{ $t("estimated-time-remaining") }}: {{ maintenanceWindow.minutesRemaining }} {{ $t("minutes") }}</span>
												</div>

												<v-progress-linear
													indeterminate
													rounded
													height="5"
													color="white"
													class="preview-progress"
												/>

												<p class="preview-hint">
													{{ $t("overlay-wait-message") }}
												</p>

												<v-btn
													text
													x-small
													class="preview-devops-btn"
													@click.stop="overlayPreviewOpen = false"
												>
													<v-icon left x-small>mdi-shield-account</v-icon>
													{{ $t("devops-bypass") }}
												</v-btn>
											</div>
										</div>
									</v-menu>
								</template>
							</v-switch>
							<v-alert v-if="form.overlay" dense type="warning" class="mt-2 mb-0 text-caption">
								{{ $t('overlay-warning') }}
							</v-alert>
						</div>
					</div>

					<!-- Scheduled Maintenance -->
					<div class="section-card">
						<div class="section-header">
							<v-icon x-small class="mr-1">mdi-calendar-clock</v-icon>
							{{ $t('scheduled-maintenance') }}
						</div>
						<div class="section-content">
							<v-row dense>
								<v-col cols="12" sm="6">
									<v-text-field
										v-model="form.maintenanceStart"
										:label="$t('start-time')"
										type="datetime-local"
										outlined
										dense
										hide-details
										:append-icon="form.maintenanceStart ? 'mdi-close' : ''"
										@click:append="form.maintenanceStart = null"
									/>
								</v-col>
								<v-col cols="12" sm="6">
									<v-text-field
										v-model="form.maintenanceEnd"
										:label="$t('end-time')"
										type="datetime-local"
										outlined
										dense
										hide-details
										:append-icon="form.maintenanceEnd ? 'mdi-close' : ''"
										@click:append="form.maintenanceEnd = null"
									/>
								</v-col>
							</v-row>
							<v-row dense class="mt-1">
								<v-col cols="6" sm="4">
									<v-text-field
										v-model.number="form.alertBeforeMins"
										:label="$t('alert-before')"
										type="number"
										suffix="min"
										outlined
										dense
										hide-details
									/>
								</v-col>
							</v-row>
							<v-alert v-if="hasScheduledMaintenance" dense :type="maintenanceAlertType" class="mt-2 mb-0 text-caption">
								{{ maintenanceAlertText }}
							</v-alert>
						</div>
					</div>
				</div>
			</v-expand-transition>
		</v-card-text>

		<v-divider />

		<v-card-actions class="pa-2">
			<v-btn text x-small @click="resetForm">
				<v-icon left x-small>mdi-refresh</v-icon>
				{{ $t('reset') }}
			</v-btn>
			<v-spacer />
			<v-btn
				color="primary"
				small
				:loading="loading"
				:disabled="!hasChanges"
				@click="saveStatus"
			>
				<v-icon left x-small>mdi-content-save</v-icon>
				{{ $t('save-changes') }}
			</v-btn>
		</v-card-actions>
	</v-card>
</template>

<script>
import { bus } from "@/main.js";

export default {
	name: "AdminForm",

	data() {
		return {
			loading: false,
			originalForm: null,
			now: Date.now(),
			nowTimer: null,
			overlayPreviewOpen: false,
			form: {
				status: "operational",
				enabled: false,
				banner: false,
				bannerText: "",
				dismissable: true,
				overlay: false,
				maintenanceStart: null,
				maintenanceEnd: null,
				alertBeforeMins: 30
			},
			statusOptions: [
				{ value: "operational", label: this.$t("status-operational"), color: "success", hex: "#4caf50", icon: "mdi-check-circle" },
				{ value: "maintenance", label: this.$t("status-maintenance"), color: "primary", hex: "#3C41C3", icon: "mdi-wrench" },
				{ value: "degraded", label: this.$t("status-degraded"), color: "warning", hex: "#FFC107", icon: "mdi-speedometer-slow" },
				{ value: "partial_outage", label: this.$t("status-partial"), color: "deep-orange", hex: "#ff5722", icon: "mdi-alert" },
				{ value: "major_outage", label: this.$t("status-major"), color: "error", hex: "#FF5252", icon: "mdi-alert-octagon" }
			]
		};
	},

	computed: {
		currentStatusConfig() {
			return this.statusOptions.find(s => s.value === this.form.status) || this.statusOptions[0];
		},

		statusPreviewClass() {
			return `status-${this.form.status || "operational"}`;
		},

		hasChanges() {
			if (!this.originalForm) return false;

			return JSON.stringify(this.form) !== JSON.stringify(this.originalForm);
		},

		hasScheduledMaintenance() {
			return this.form.maintenanceStart && this.form.maintenanceEnd;
		},

		maintenanceWindow() {
			if (!this.hasScheduledMaintenance) return null;

			const now = this.now;
			const start = new Date(this.form.maintenanceStart).getTime();
			const end = new Date(this.form.maintenanceEnd).getTime();
			const alertBefore = (this.form.alertBeforeMins || 0) * 60 * 1000;

			return {
				isInWindow: now >= start && now <= end,
				isInAlertWindow: now >= start - alertBefore && now < start,
				isPast: now > end,
				isFuture: now < start - alertBefore,
				minutesUntilStart: Math.ceil((start - now) / 60000),
				minutesRemaining: Math.ceil((end - now) / 60000)
			};
		},

		maintenanceAlertType() {
			if (!this.maintenanceWindow) return "info";
			if (this.maintenanceWindow.isInWindow) return "error";
			if (this.maintenanceWindow.isInAlertWindow) return "warning";
			if (this.maintenanceWindow.isPast) return "success";

			return "info";
		},

		maintenanceAlertText() {
			if (!this.maintenanceWindow) return "";

			const mw = this.maintenanceWindow;

			if (mw.isInWindow) {
				return this.$t("maintenance-in-progress", { minutes: mw.minutesRemaining });
			}
			if (mw.isInAlertWindow) {
				return this.$t("maintenance-starting-soon", { minutes: mw.minutesUntilStart });
			}
			if (mw.isPast) {
				return this.$t("maintenance-completed");
			}

			return this.$t("maintenance-scheduled", { minutes: mw.minutesUntilStart });
		},

		maintenanceInfo() {
			if (!this.maintenanceWindow) return null;

			const mw = this.maintenanceWindow;

			if (mw.isInWindow) {
				return `${mw.minutesRemaining} min ${this.$t("remaining")}`;
			}
			if (mw.isInAlertWindow) {
				return `${this.$t("starts-in")} ${mw.minutesUntilStart} min`;
			}

			return null;
		},

		previewCountdownText() {
			if (!this.maintenanceWindow) return null;

			const mw = this.maintenanceWindow;

			if (mw.isInWindow) {
				const mins = mw.minutesRemaining;

				return mins === 1
					? `1 ${this.$t("minute-remaining")}`
					: `${mins} ${this.$t("minutes-remaining")}`;
			}
			if (mw.isInAlertWindow) {
				const mins = mw.minutesUntilStart;

				return mins === 1
					? `${this.$t("starts-in")} 1 ${this.$t("minute")}`
					: `${this.$t("starts-in")} ${mins} ${this.$t("minutes")}`;
			}

			return null;
		},

		effectiveBanner() {
			if (!this.form.enabled) return false;

			const mw = this.maintenanceWindow;

			return this.form.banner || mw?.isInWindow || mw?.isInAlertWindow;
		},

		effectiveOverlay() {
			if (!this.form.enabled) return false;

			return this.form.overlay && this.maintenanceWindow?.isInWindow;
		},

		previewStatusClass() {
			return `preview-status-${this.form.status || "maintenance"}`;
		},

		previewOverlayTitle() {
			const titles = {
				operational: "status-operational",
				maintenance: "status-maintenance-title",
				degraded: "status-degraded-title",
				partial_outage: "status-partial-title",
				major_outage: "status-major-title"
			};

			return this.$t(titles[this.form.status] || titles.maintenance);
		}
	},

	async mounted() {
		await this.fetchStatus();
		// Update countdown every 30 seconds
		this.nowTimer = setInterval(() => {
			this.now = Date.now();
		}, 30000);
	},

	beforeDestroy() {
		if (this.nowTimer) {
			clearInterval(this.nowTimer);
		}
	},

	methods: {
		async fetchStatus() {
			try {
				const { data } = await this.axios.get("status");

				// Map API response to form
				this.form = {
					status: data.status || "operational",
					enabled: data.enabled || false,
					banner: data.banner || false,
					bannerText: data.bannerText || "",
					dismissable: data.dismissable !== false,
					overlay: data.overlay || false,
					maintenanceStart: data.maintenanceStart || null,
					maintenanceEnd: data.maintenanceEnd || null,
					alertBeforeMins: data.alertBeforeMins ?? 30
				};

				this.originalForm = JSON.parse(JSON.stringify(this.form));
			} catch (err) {
				console.error("Failed to fetch status:", err);
				this.$error(this.$t("error.fetch-status"));
			}
		},

		resetForm() {
			if (this.originalForm) {
				this.form = JSON.parse(JSON.stringify(this.originalForm));
			}
		},

		async saveStatus() {
			this.loading = true;

			try {
				await this.axios.put("status", this.form);

				this.originalForm = JSON.parse(JSON.stringify(this.form));

				this.$success(this.$t("status-saved"));

				// Emit event for parent and bus to refresh status immediately
				this.$emit("status-updated");
				bus.$emit("refresh-status");
			} catch (err) {
				console.error("Failed to save status:", err);
				this.$error(this.$t("error.save-status"));
			} finally {
				this.loading = false;
			}
		}
	}
};
</script>

<style scoped>
.status-admin-card {
	overflow: hidden;
}

@media (max-width: 599px) {
	.status-admin-card {
		overflow-y: auto;
		height: 100%;
	}
}

.preview-wrapper {
	position: relative;
}

.preview-label {
	position: absolute;
	top: 0px;
	right: 8px;
	font-size: 9px;
	text-transform: uppercase;
	letter-spacing: 0.5px;
	color: rgba(255, 255, 255, 0.5);
	z-index: 1;
	pointer-events: none;
}

.status-preview {
	position: relative;
	display: flex;
	align-items: center;
	gap: 8px;
	padding: 8px 12px;
	border-radius: 6px;
	background: linear-gradient(135deg, var(--preview-bg) 0%, var(--preview-bg-dark) 100%);
	color: white;
	transition: background 0.3s ease;
}

.preview-text {
	flex: 1;
	font-size: 13px;
	font-weight: 500;
	line-height: 1.4;
	margin-right: 50px;
}

.dismiss-icon {
	position: absolute;
	right: 12px;
	top: 50%;
	transform: translateY(-50%);
	opacity: 0.7;
}

.countdown-badge {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	background: rgba(255, 255, 255, 0.2);
	padding: 2px 8px;
	border-radius: 10px;
	font-size: 11px;
	font-weight: 600;
	flex-shrink: 0;
	white-space: nowrap;
}

.status-preview.status-operational {
	--preview-bg: #4caf50;
	--preview-bg-dark: #388e3c;
}

.status-preview.status-maintenance {
	--preview-bg: #3C41C3;
	--preview-bg-dark: #2e3199;
}

.status-preview.status-degraded {
	--preview-bg: #FFC107;
	--preview-bg-dark: #FFA000;
}

.status-preview.status-partial_outage {
	--preview-bg: #ff5722;
	--preview-bg-dark: #e64a19;
}

.status-preview.status-major_outage {
	--preview-bg: #FF5252;
	--preview-bg-dark: #FF1744;
}

.master-toggle {
	background: #f5f5f5;
	border-radius: 6px;
	padding: 6px 12px;
}

.section-card {
	border: 1px solid #e0e0e0;
	border-radius: 6px;
	overflow: hidden;
}

.section-header {
	display: flex;
	align-items: center;
	padding: 6px 10px;
	background: #fafafa;
	font-size: 11px;
	font-weight: 600;
	color: #424242;
	border-bottom: 1px solid #e0e0e0;
}

.section-content {
	padding: 8px 10px;
}

.status-buttons {
	display: flex;
	gap: 6px;
	padding: 8px 10px;
	flex-wrap: wrap;
}

.status-btn {
	display: flex;
	align-items: center;
	gap: 4px;
	padding: 4px 10px;
	border: 1px solid #e0e0e0;
	border-radius: 14px;
	background: white;
	cursor: pointer;
	transition: all 0.2s ease;
	font-size: 11px;
	font-weight: 500;
	color: #616161;
}

.status-btn:hover {
	border-color: #bdbdbd;
	background: #fafafa;
}

.status-btn.active {
	border-color: var(--active-color);
	background: var(--active-color);
	color: white;
}

.toggle-row {
	display: flex;
	align-items: center;
	flex-wrap: wrap;
}

.compact-switch {
	transform: scale(0.85);
	transform-origin: left center;
}

/* Overlay Preview Styles - matches StatusOverlay exactly */
.overlay-preview-wrapper {
	position: relative;
	background: linear-gradient(135deg, #1a237e 0%, #0d47a1 50%, #01579b 100%);
	border-radius: 12px;
	overflow: hidden;
}

.overlay-preview-wrapper.preview-status-maintenance {
	background: linear-gradient(135deg, #1a1a4e 0%, #3C41C3 50%, #2e3199 100%);
}

.overlay-preview-wrapper.preview-status-degraded {
	background: linear-gradient(135deg, #5c4600 0%, #FFC107 50%, #FFA000 100%);
}

.overlay-preview-wrapper.preview-status-partial_outage {
	background: linear-gradient(135deg, #4a1c0d 0%, #ff5722 50%, #e64a19 100%);
}

.overlay-preview-wrapper.preview-status-major_outage {
	background: linear-gradient(135deg, #4a0d0d 0%, #FF5252 50%, #FF1744 100%);
}

.overlay-preview-wrapper.preview-status-operational {
	background: linear-gradient(135deg, #1b3d1b 0%, #4caf50 50%, #388e3c 100%);
}

.overlay-preview-label {
	position: absolute;
	top: 6px;
	right: 10px;
	font-size: 9px;
	text-transform: uppercase;
	letter-spacing: 0.5px;
	color: rgba(255, 255, 255, 0.5);
	z-index: 1;
}

.overlay-preview-content {
	display: flex;
	flex-direction: column;
	align-items: center;
	text-align: center;
	padding: 24px;
	color: white;
	min-width: 320px;
}

.preview-icon-container {
	display: flex;
	align-items: center;
	justify-content: center;
	width: 80px;
	height: 80px;
	border-radius: 50%;
	margin-bottom: 16px;
	animation: pulse 2s infinite;
}

.preview-icon-container.preview-status-maintenance {
	background: linear-gradient(135deg, #3C41C3 0%, #2e3199 100%);
}

.preview-icon-container.preview-status-degraded {
	background: linear-gradient(135deg, #FFC107 0%, #FFA000 100%);
}

.preview-icon-container.preview-status-partial_outage {
	background: linear-gradient(135deg, #ff5722 0%, #e64a19 100%);
}

.preview-icon-container.preview-status-major_outage {
	background: linear-gradient(135deg, #FF5252 0%, #FF1744 100%);
}

.preview-icon-container.preview-status-operational {
	background: linear-gradient(135deg, #4caf50 0%, #388e3c 100%);
}

@keyframes pulse {
	0%, 100% {
		transform: scale(1);
		box-shadow: 0 0 0 0 rgba(255, 255, 255, 0.4);
	}
	50% {
		transform: scale(1.05);
		box-shadow: 0 0 0 15px rgba(255, 255, 255, 0);
	}
}

.preview-title {
	font-size: 18px;
	font-weight: 600;
	margin-bottom: 10px;
}

.preview-message {
	font-size: 13px;
	color: rgba(255, 255, 255, 0.9);
	margin-bottom: 16px;
	line-height: 1.5;
}

.preview-countdown {
	display: flex;
	align-items: center;
	background: rgba(255, 255, 255, 0.15);
	padding: 8px 16px;
	border-radius: 20px;
	margin-bottom: 16px;
	font-size: 12px;
	font-weight: 500;
}

.preview-progress {
	width: 140px;
	margin-bottom: 16px;
}

.preview-hint {
	font-size: 11px;
	color: rgba(255, 255, 255, 0.6);
	margin-bottom: 12px;
}

.preview-devops-btn {
	opacity: 0.9;
	text-transform: none;
	color: white !important;
	background: rgba(255, 255, 255, 0.15) !important;
	border: 1px solid rgba(255, 255, 255, 0.3);
	letter-spacing: 0;
}

.preview-devops-btn:hover {
	opacity: 1;
	background: rgba(255, 255, 255, 0.25) !important;
}
</style>